import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import ShopScheduleTimeline from '../components/pm/ShopScheduleTimeline';
import { getShopScheduleViewModel } from '../utils/shopScheduleView';
import { AlertTriangle, LayoutGrid, Monitor } from 'lucide-react';
import './ProductionSchedulePage.css';
import './ShopBigScreenPage.css';

const ROW_H = 52;
const CELL = 22;
const LABEL_W = 300;

const ShopBigScreenPage: React.FC = () => {
  const { timberProjects, projectTasks } = useCRM();
  useCRMCollections(['projectTasks']);
  const [onlyOverlaps, setOnlyOverlaps] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const model = useMemo(
    () => getShopScheduleViewModel(projectTasks, timberProjects),
    [projectTasks, timberProjects],
  );

  const { stats } = model;

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const timeLabel = now.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className="shop-display">
      <header className="shop-display-header">
        <div className="shop-display-brand">
          <Monitor size={28} className="shop-display-brand-icon" aria-hidden />
          <div>
            <h1 className="shop-display-title">Shop schedule</h1>
            <p className="shop-display-tagline">Production & installation — big screen</p>
          </div>
        </div>
        <div className="shop-display-header-right">
          <time className="shop-display-clock" dateTime={now.toISOString()}>
            {timeLabel}
          </time>
          <label className="shop-display-filter">
            <input type="checkbox" checked={onlyOverlaps} onChange={(e) => setOnlyOverlaps(e.target.checked)} />
            Overlaps only
          </label>
          <Link to="/shop-schedule" className="shop-display-link">
            <LayoutGrid size={16} aria-hidden />
            Normal view
          </Link>
          <Link to="/project-hub" className="shop-display-link shop-display-link--ghost">
            Project hub
          </Link>
        </div>
      </header>

      {stats.overlapPairCount > 0 && (
        <div className="shop-display-alert" role="alert">
          <AlertTriangle size={22} aria-hidden />
          <span>
            <strong>{stats.overlapPairCount}</strong> overlapping task pair{stats.overlapPairCount === 1 ? '' : 's'} — check CNC / shop capacity.
          </span>
        </div>
      )}

      <section className="shop-display-dashboard" aria-label="Schedule summary">
        <div className="shop-display-stat">
          <span className="shop-display-stat-label">Production tasks</span>
          <span className="shop-display-stat-value">{stats.productionCount}</span>
        </div>
        <div className="shop-display-stat">
          <span className="shop-display-stat-label">Installation tasks</span>
          <span className="shop-display-stat-value">{stats.installationCount}</span>
        </div>
        <div className={`shop-display-stat ${stats.overlapPairCount > 0 ? 'shop-display-stat--warn' : ''}`}>
          <span className="shop-display-stat-label">Overlap pairs</span>
          <span className="shop-display-stat-value">{stats.overlapPairCount}</span>
        </div>
        <div className="shop-display-stat">
          <span className="shop-display-stat-label">Projects</span>
          <span className="shop-display-stat-value">{stats.projectCount}</span>
        </div>
        <div className="shop-display-stat">
          <span className="shop-display-stat-label">Starting this week</span>
          <span className="shop-display-stat-value">{stats.tasksStartingThisWeek}</span>
        </div>
        <div className="shop-display-stat">
          <span className="shop-display-stat-label">Ending this week</span>
          <span className="shop-display-stat-value">{stats.tasksEndingThisWeek}</span>
        </div>
      </section>

      <section className="shop-display-timeline" aria-label="Timeline by project">
        <ShopScheduleTimeline
          model={model}
          onlyOverlaps={onlyOverlaps}
          cellWidth={CELL}
          rowHeight={ROW_H}
          labelWidth={LABEL_W}
          displayMode
        />
      </section>
    </div>
  );
};

export default ShopBigScreenPage;
